import { useState } from "react";
import { AdminLayout } from "../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  ShieldCheck,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  FileCheck,
  AlertTriangle
} from "lucide-react";

interface AdminVerifyProps {
  onNavigate: (page: string) => void;
}

/**
 * Admin Verify Page
 * Manage patient verification requests
 */
export function AdminVerify({ onNavigate }: AdminVerifyProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Mock verification requests
  const verificationRequests = [
    {
      id: "1",
      patientName: "John Smith",
      patientEmail: "john.smith@example.com",
      clinicName: "Sapphire Medical Clinic",
      docType: "prescription",
      submittedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      status: "pending",
    },
    {
      id: "2",
      patientName: "Sarah Johnson",
      patientEmail: "sarah.j@example.com",
      clinicName: "Curaleaf Clinic",
      docType: "clinic_letter",
      submittedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      status: "pending",
    },
    {
      id: "3",
      patientName: "Mike Williams",
      patientEmail: "mike.w@example.com",
      clinicName: "Cannabis Clinic Cardiff",
      docType: "medical_card",
      submittedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      status: "approved",
    },
  ];

  const stats = [
    { label: "Pending", value: 2, icon: Clock, color: "text-amber-600" },
    { label: "Approved Today", value: 8, icon: CheckCircle, color: "text-primary" },
    { label: "Total Verified", value: 234, icon: Users, color: "text-muted-foreground" },
    { label: "Rejected", value: 12, icon: XCircle, color: "text-destructive" },
  ];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="border-amber-500 text-amber-700">Pending</Badge>;
      case 'approved':
        return <Badge variant="default" className="bg-primary">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <AdminLayout currentPage="admin-verify" onNavigate={onNavigate}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl">Patient Verification</h1>
            <p className="text-muted-foreground">Review and approve patient verification requests</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                      <p className="text-2xl font-bold mt-1">{stat.value}</p>
                    </div>
                    <Icon className={`w-8 h-8 ${stat.color}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Search & Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Verification Queue</CardTitle>
            <CardDescription>Review pending patient verification requests</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline">Filter</Button>
            </div>

            <Alert>
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription className="text-sm">
                <strong>Note:</strong> Manual verification is used when QR token verification is not available. Always verify clinic affiliation before approving.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Verification Requests */}
        <div className="space-y-4">
          {verificationRequests.map((request) => (
            <Card key={request.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{request.patientName}</h3>
                        <p className="text-sm text-muted-foreground">{request.patientEmail}</p>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Clinic</p>
                        <p className="font-medium">{request.clinicName}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Document Type</p>
                        <p className="font-medium capitalize">{request.docType.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Submitted</p>
                        <p className="font-medium">{formatDate(request.submittedAt)}</p>
                      </div>
                    </div>

                    {request.status === 'pending' && (
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" variant="default" className="gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" className="gap-2">
                          <FileCheck className="w-4 h-4" />
                          View Document
                        </Button>
                        <Button size="sm" variant="outline" className="gap-2 text-destructive hover:text-destructive">
                          <XCircle className="w-4 h-4" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
